import { Fragment } from "react";
import { useSelector } from "react-redux";

import { selectUser } from "../lib/scraper.slice";
import {
    CONTEXT_TYPES,
    context_types,
    USER_ROLES
} from "../lib/consts";
import {
    ContextType,
    IContextNoUUID,
    IExtractParams,
    PreprocessExcelStrategies,
    PreprocessOcrOrientationStrategies,
    PreprocessOcrRotationStrategies,
    PreprocessOcrTableStrategies
} from "../lib/types";
import { DEFAULT_NEW_CONTEXT } from "../screens/NewTemplate";

import { Checkbox } from "./Checkbox";
import { Dropdown } from "./Dropdown";
import { Textbox } from "./Textbox";


type ContextProperties = "type" | "extraction_strategy" | "preprocess_excel_strategy" |
     "preprocess_ocr_strategy" | "orientation_segments_strategy" | "preprocess_ocr_table_strategy" |
    "max_partial_responses" | "prompt_output_format" | "try_auto_heal"

type ContextDetailsAdminSettingsProps = {
    context: IContextNoUUID;
    show_admin_details: boolean;
    isDisabled: boolean;
    setType?: (type: ContextType) => void;
    setExtractParams: (extract_params: IExtractParams) => void;
}

export function ContextDetailsAdminSettings({ context, show_admin_details, isDisabled, setType, setExtractParams }: ContextDetailsAdminSettingsProps) {
    const user = useSelector(selectUser);
    const is_admin = user.role === USER_ROLES.admin;

    const handleContextPropertyChange = (property: ContextProperties, value: string | boolean | undefined) => {
        if (setType && property === "type") {
            setType(value as ContextType);
        } else if (property === "extraction_strategy") {
            setExtractParams({ ...context.extract_params, extraction_strategy: value as "standard" | "prepend_header_page" | "large_tables_small_chunks" | "large_tables_medium_chunks" | "large_tables_large_chunks" });
        } else if (property === "preprocess_excel_strategy") {
            setExtractParams({ ...context.extract_params, preprocess_excel_strategy: value as PreprocessExcelStrategies | undefined });
        } else if (property === "preprocess_ocr_strategy") {
            setExtractParams({ ...context.extract_params, preprocess_ocr_strategy: value as PreprocessOcrRotationStrategies | undefined });
        } else if (property === "orientation_segments_strategy") {
            setExtractParams({ ...context.extract_params, orientation_segments_strategy: value as PreprocessOcrOrientationStrategies | undefined });
        } else if (property === "preprocess_ocr_table_strategy") {
            setExtractParams({ ...context.extract_params, preprocess_ocr_table_strategy: value as PreprocessOcrTableStrategies | undefined });
        } else if (property === "max_partial_responses") {
            const int_value = parseInt(value as string, 10);
            // hardcoded upper limit
            const final_value = (!isNaN(int_value) && 0 < int_value && int_value <= 20) ? int_value : DEFAULT_NEW_CONTEXT.extract_params.max_partial_responses;
            setExtractParams({ ...context.extract_params, max_partial_responses: final_value });
        } else if (property === "prompt_output_format") {
            setExtractParams({ ...context.extract_params, prompt_output_format: value as "tsv" | "json" });
        } else if (property === "try_auto_heal") {
            // only allow auto heal if we have TSV output
            const final_value = value as boolean && context.extract_params.prompt_output_format === "tsv";
            setExtractParams({ ...context.extract_params, try_auto_heal: final_value });
        }
    }

    const is_lookup_table = context.type === CONTEXT_TYPES.lookup_table;
    const is_classifier = context.type === CONTEXT_TYPES.classifier;

    return <Fragment>
        {show_admin_details && is_admin && <div className="sm:grid sm:grid-cols-4 max-w-5xl sm:items-start sm:gap-4 sm:pt-6 border-t border-gray-900/10 text-sm font-bold leading-6 text-gray-400">
            Admin settings
        </div>}

        {!is_classifier && show_admin_details && is_admin && <div className="sm:grid sm:grid-cols-4 max-w-5xl sm:items-start sm:gap-4 sm:py-6">
            {!is_lookup_table && <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                How many rows do you expect?
            </label>}
            {!is_lookup_table && <div className="mt-2 sm:mt-0">
                <Dropdown
                    values={["Many", "One", "Hierarchical"]}
                    ids={context_types}
                    selected={context.type}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("type", id)} />
            </div>}

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                Extraction strategy
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    values={["Standard extraction", "Prepend header pages", "Large tables (small chunks)", "Large tables (medium chunks)", "Large tables (large chunks)"]}
                    ids={["standard", "prepend_header_page", "large_tables_small_chunks", "large_tables_medium_chunks", "large_tables_large_chunks"]}
                    selected={context.extract_params.extraction_strategy}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("extraction_strategy", id)} />
            </div>

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                Excel preprocessing strategy
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    ids={["derive_from_template", "col_names_sparse", "col_names_sparse_zero", "col_names_sparse_zero_extended", "col_names_dense_zero", "col_names_dense_empty", "without_col_names"]}
                    values={["Inherit from template", "Column names (sparse)", "Column names with zeros removed (sparse)", "Column names with zeros and dashes removed (sparse)", "Column names (dense with zero)", "Column names (dense with empty)", "Without column names"]}
                    selected={context.extract_params.preprocess_excel_strategy || "derive_from_template"}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("preprocess_excel_strategy", id === "derive_from_template" ? undefined : id)} />
            </div>

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                OCR preprocessing strategy
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    ids={["derive_from_template", "simple", "fix_rotation"]}
                    values={["Inherit from template", "Simple", "Fix rotations"]}
                    selected={context.extract_params.preprocess_ocr_strategy || "derive_from_template"}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("preprocess_ocr_strategy", id === "derive_from_template" ? undefined : id)} />
            </div>

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                Rotation-segment handling strategy
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    ids={["derive_from_template", "as_is", "only_main", "segment"]}
                    values={["Inherit from template", "Keep rotation as they are", "Only keep the main rotations", "Segment texts by rotation"]}
                    selected={context.extract_params.orientation_segments_strategy || "derive_from_template"}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("orientation_segments_strategy", id === "derive_from_template" ? undefined : id)} />
            </div>

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                OCR table-handling strategy
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    ids={["derive_from_template", "plain_text_only", "markdown_only", "markdown_and_plain_text"]}
                    values={["Inherit from template", "Use normal text only", "Use markdown only", "Use markdown and normal text"]}
                    selected={context.extract_params.preprocess_ocr_table_strategy || "derive_from_template"}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("preprocess_ocr_table_strategy", id === "derive_from_template" ? undefined : id)} />
            </div>

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                Maximum partial responses
            </label>
            <div className="mt-2 sm:mt-0">
                <Textbox
                    placeholder="0-20"
                    disabled={isDisabled}
                    value={context.extract_params.max_partial_responses.toString()}
                    onChange={(value) => handleContextPropertyChange("max_partial_responses", value)} />
            </div>

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                Prompt output format
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    values={["JSON", "TSV"]}
                    ids={["json", "tsv"]}
                    selected={context.extract_params.prompt_output_format}
                    disabled={isDisabled}
                    onChange={(id) => handleContextPropertyChange("prompt_output_format", id)} />
            </div>

            <label htmlFor="try_auto_heal" className="block text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                Try auto-heal
            </label>
            <div className="mt-2 sm:mt-0">
                <Checkbox
                    id="try_auto_heal"
                    checked={context.extract_params.try_auto_heal}
                    disabled={isDisabled}
                    setChecked={(value) => handleContextPropertyChange("try_auto_heal", value)} />
            </div>

        </div>}
    </Fragment>;
}
